import { isValid, parseISO } from 'date-fns';
import type { TFunction } from 'i18next';
import * as yup from 'yup';

import type { Asset } from '@/types/assets';

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

const isValidISODate = (value: string) =>
  ISO_DATE_RE.test(value.trim()) && isValid(parseISO(value.trim()));

// Accepts both ISO date strings (CSV flow) and Date objects (form DatePickers).
const isValidDateValue = (value: unknown): boolean => {
  if (value instanceof Date) return isValid(value);
  if (typeof value === 'string') return isValidISODate(value);
  return false;
};

export const getAssetSchema = (t: TFunction) =>
  yup.object<Asset>().shape({
    name: yup.string().required(t('assetSchema.nameRequired')),
    productType: yup
      .object()
      .nullable()
      .required(t('assetSchema.assetTypeRequired'))
      .shape({
        value: yup.string(),
      }),
    purchasePrice: yup
      .string()
      .nullable()
      .test('isValidPrice', t('assetSchema.invalidPriceFormat'), value => {
        if (!value) return true;
        // Value must be a plain decimal number (Python Decimal format):
        // digits only with an optional decimal point — no commas, dollar signs, or other characters
        const regex = /^\d+(\.\d+)?$/;
        return regex.test(value.trim());
      }),
    purchasedFrom: yup
      .string()
      .nullable()
      .test('isValidUrl', t('assetSchema.invalidUrlFormat'), value => {
        if (!value) return true;
        try {
          new URL(value);
          return true;
        } catch {
          return false;
        }
      }),
    purchaseDate: yup
      .mixed()
      .nullable()
      .test('isValidDate', t('assetSchema.invalidDateFormat'), value => {
        if (!value) return true;
        return isValidDateValue(value);
      }),
    warrantyExpirationDate: yup
      .mixed()
      .nullable()
      .test('isValidDate', t('assetSchema.invalidDateFormat'), value => {
        if (!value) return true;
        return isValidDateValue(value);
      }),
  });
